import { useState } from "react";
import { useTranslation } from "react-i18next";
import usePageMeta from "../hooks/usePageMeta";
import Footer from "../components/Footer";
import ContactCTA from "../components/ContactCTA";
import { MapPin, Phone, Mail, Globe, Send, CheckCircle, AlertCircle } from "lucide-react";

// ---------------------------------------------------------------------------
// No backend needed. Paste a form-service endpoint here to receive submissions
// straight into your inbox. Works with Formspree / Web3Forms / Getform.
//   Formspree:  https://formspree.io/f/xxxxxxxx
//   Web3Forms:  https://api.web3forms.com/submit   (add access_key to the body)
// Leave it empty and the form falls back to opening the visitor's email client
// with the message pre-filled.
// ---------------------------------------------------------------------------
const FORM_ENDPOINT = "";

export default function Contact() {
  const { t } = useTranslation();
  usePageMeta({
    title: "Contact | Sky Global",
    description: t("contact.description"),
    path: "/contact",
  });

  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const to = t("contact.emailText");
  const subject = `Website inquiry${form.company ? ` — ${form.company}` : ""}`;
  const bodyText = `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    if (FORM_ENDPOINT) {
      try {
        const res = await fetch(FORM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ ...form, _subject: subject, _replyto: form.email }),
        });
        if (!res.ok) throw new Error("request failed");
        setStatus("ok");
        setForm({ name: "", email: "", company: "", message: "" });
        return;
      } catch {
        setStatus("error");
        return;
      }
    }

    // Fallback (no endpoint configured): open Gmail compose in a new tab so it
    // works even when the visitor has no desktop mail client configured.
    const url =
      `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}` +
      `&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setStatus("ok");
  };

  const items = [
    { icon: MapPin, label: t("contact.address"), value: t("contact.addressText") },
    { icon: Phone, label: t("contact.phone"), value: t("contact.phoneText"), href: "tel:+6281181288777" },
    { icon: Mail, label: t("contact.email"), value: t("contact.emailText"), href: `mailto:${t("contact.emailText")}` },
    { icon: Globe, label: "Website", value: t("contact.website"), href: `https://${t("contact.website")}`, external: true },
  ];

  const fieldClass =
    "w-full rounded-lg border border-line bg-white px-4 py-3 text-sm text-black placeholder:text-gray-500 transition-colors focus:border-textBlue focus-visible:ring-2 focus-visible:ring-textBlue/40";

  return (
    <>
      {/* Header */}
      <section className="relative overflow-hidden page-header pt-40 tablet:pt-36 mobile:pt-32 pb-20 tablet:pb-16 px-6 mobile:px-4">
        <div
          className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,#000_25%,transparent_70%)]"
          aria-hidden="true"
        ></div>
        <div className="relative container-page">
          <span className="kicker mb-4 animate-fade-in">{t("navbar.contact")}</span>
          <h1 className="text-5xl tablet:text-4xl mobile:text-3xl font-semibold tracking-[-0.02em] text-black leading-[1.05] max-w-3xl mb-4 animate-slide-up">
            {t("contact.heading")}
          </h1>
          <p className="text-lg tablet:text-base text-gray-700 leading-relaxed max-w-[60ch]">
            {t("contact.description")}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 tablet:py-20 px-6 mobile:px-4 bg-white">
        <div className="container-page grid grid-cols-2 tablet:grid-cols-1 gap-16 tablet:gap-12">
          {/* Left — Contact Info */}
          <div>
            <h2 className="text-3xl tablet:text-2xl font-semibold tracking-[-0.02em] text-black mb-2">
              Get In Touch
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8 max-w-[52ch]">
              {t("contact.title")}
            </p>

            <div className="space-y-4">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="flex items-start gap-4 rounded-xl border border-line bg-surface p-5 transition-colors duration-200 hover:border-line-strong"
                  >
                    <span className="bg-blueLight w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-textBlue" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-black mb-1">{item.label}</h3>
                      {item.href ? (
                        <a
                          href={item.href}
                          {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="text-textBlue hover:text-textBlueDark hover:underline transition-colors break-words"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-gray-600 leading-relaxed">{item.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right — Contact Form */}
          <div className="rounded-2xl border border-line bg-surface p-8 mobile:p-6">
            <h2 className="text-2xl font-semibold tracking-[-0.02em] text-black mb-6">
              {t("contact.formTitle")}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 tablet:grid-cols-1 gap-4">
                <div>
                  <label htmlFor="cf-name" className="block text-sm font-medium text-black mb-1.5">
                    {t("contact.name")}
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder={`${t("contact.name")}…`}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-sm font-medium text-black mb-1.5">
                    {t("contact.emailInput")}
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    spellCheck={false}
                    required
                    value={form.email}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="name@company.com…"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cf-company" className="block text-sm font-medium text-black mb-1.5">
                  {t("contact.company")}
                </label>
                <input
                  id="cf-company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  value={form.company}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder={`${t("contact.company")}…`}
                />
              </div>

              <div>
                <label htmlFor="cf-message" className="block text-sm font-medium text-black mb-1.5">
                  {t("contact.message")}
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  className={`${fieldClass} resize-y`}
                  placeholder={`${t("contact.message")}…`}
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className="group inline-flex items-center gap-2 bg-textBlue text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-textBlueDark transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2"
              >
                <Send className="w-4 h-4" aria-hidden="true" />
                {status === "sending" ? "Sending…" : t("contact.submit")}
              </button>

              <p aria-live="polite" className="text-sm min-h-[1.25rem]">
                {status === "ok" && (
                  <span className="inline-flex items-center gap-1.5 text-emerald-600">
                    <CheckCircle className="w-4 h-4" aria-hidden="true" />
                    Thanks — a draft opened in your email app. Send it to reach us.
                  </span>
                )}
                {status === "error" && (
                  <span className="inline-flex items-center gap-1.5 text-red-600">
                    <AlertCircle className="w-4 h-4" aria-hidden="true" />
                    Something went wrong. Please email us at {t("contact.emailText")}.
                  </span>
                )}
              </p>
            </form>
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </>
  );
}
